import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Employee, getEmployeeTypeDisplayName } from '../services/api.ts';
import { recolorOptionsFor, NAMEPLATE_COLORS, needsNerveBlock, NERVE_BLOCK_BORDER } from './staffNameplates.ts';
import { darkenHex } from './hospitalColors.ts';
import type { TransferCandidateGroup, TransferCandidateEntry } from './roomTransfers.ts';

// One room an anesthesiologist ended up covering after the algorithm settled: the room label, its
// earliest case start, the case types in it, and whether a CRNA is there (supervising vs solo).
export interface AssignedRoomInfo { operatingRoomId: string; roomLabel: string; startTimeUtc: string; surgeryTypes: string[]; hasCrna: boolean; cases?: { startTimeUtc: string; surgeryType: string }[]; }

// A hospital "coin": the site's acronym + its color (same coins used on the board & the tabs).
export interface SiteCoinOption { hospitalId: string; name: string; acronym: string; color: string; }
// Editable site coin (hard lock for the day): click a site to lock this person there; click the locked one to clear.
export interface SiteLockControl { current?: string; options: SiteCoinOption[]; onPick: (hospitalId: string | null) => void; }
// Room transfer: move one of this doctor's rooms to another doctor (changes both doctors' in-times). Local &
// reversible. `onMoveAll` hands EVERY room this doctor owns to one target; `onSwapAll` EXCHANGES all room
// assignments between this doctor and another (swapping their in-times — handy to reorder pecking in-times).
export interface RoomTransferControl {
  candidates: { id: string; name: string }[];   // FLAT fallback targets for move-all / swap-all
  // Optional GROUPED targets for the move-all / swap-all dropdowns: doctors organized by the site(s) they're
  // assigned at, with a "Not yet assigned" section last. When present, the dropdowns render these as optgroups.
  candidateGroups?: TransferCandidateGroup[];
  onMove: (operatingRoomId: string, toDoctorId: string) => void;
  onMoveAll: (toDoctorId: string) => void;
  onSwapAll: (otherDoctorId: string) => void;
  // Per-room, site-scoped targets (doctors at THAT room's site or locked there). Falls back to `candidates`.
  candidatesForRoom?: (operatingRoomId: string) => { id: string; name: string }[];
  // Swap just this one room with another doctor (trade for their earliest room at the same site).
  onSwapRoom?: (operatingRoomId: string, otherDoctorId: string) => void;
  // Optional: full read-only card props for a candidate, so the picker can open THEIR staff card (credentials,
  // rooms) when a name is tapped — the user confirms the transfer/swap from that card. Computed lazily (only
  // for the one open picker), so it may run per-candidate without a whole-tab cost. Omit `children`.
  getCandidateCard?: (id: string) => Omit<EmployeeDetailsPopoverProps, 'children'> | undefined;
}

// "Assign relief" (board only): place a relief nameplate above THIS person in THIS one OR slot. The picker is
// grouped like the bullpen (shift window / pecking order). Doctor cards list doctors; CRNA cards list doctors
// AND CRNAs. Local & reversible (drag the relief plate off, or Clear).
export interface ReliefPickerItem { id: string; name: string; note?: string; color?: string; }
export interface ReliefPickerGroup { label: string; items: ReliefPickerItem[]; }
export interface AssignReliefControl {
  groups: ReliefPickerGroup[];
  current?: { id: string; name: string };
  onAssign: (employeeId: string) => void;
  onClear: () => void;
}

// The chief's per-day disposition for a doctor, set from this card. Mutually exclusive:
//  - 'meeting'  → brown tag + a notes box; notes surface under the name on the Anesthesiologists tab.
//  - 'request'  → periwinkle tag + a notes box; notes surface under the name on the Anesthesiologists tab.
//  - 'off'      → sent home; the name stays on the tab but greyed out, reversible by clearing this.
export type DoctorDayStatus = 'meeting' | 'request' | 'off';
const DOCTOR_DAY_STATUSES: DoctorDayStatus[] = ['meeting', 'request', 'off'];
// Dispositions are multi-select (a doctor can have e.g. Meeting AND Request at once). Read a stored value
// tolerantly — the legacy single string OR the newer list — as a clean list of valid statuses, so days saved
// before this change keep working on both the board and the Anesthesiologists tab.
export const normalizeDoctorStatuses = (raw: any): DoctorDayStatus[] => {
  const arr = Array.isArray(raw) ? raw : (raw ? [raw] : []);
  return arr.filter((s: any): s is DoctorDayStatus => DOCTOR_DAY_STATUSES.includes(s));
};
export interface DoctorStatusControl {
  statuses: DoctorDayStatus[];
  note: string;
  onToggleStatus: (status: DoctorDayStatus) => void;
  onSetNote: (note: string) => void;
}

const REPORT_IN_LEAD_MIN = 30;   // report ~30 min before the first case
const NERVE_BLOCK_LEAD_MIN = 45; // 45 min if that first case needs a nerve block

const MEETING_COLOR = '#4a2c10';                       // dark brown — "has a meeting today" (stands out on orange)
const REQUEST_COLOR = NAMEPLATE_COLORS.request;        // periwinkle — "request case"
const OFF_COLOR = '#6b7280';                            // slate gray — "sent home / off"
const SUPERVISE_COLOR = NAMEPLATE_COLORS.docSupervise;  // blue — supervising a CRNA
const SOLO_COLOR = NAMEPLATE_COLORS.docSolo;            // orange — in a room solo

// Live supervision tally for an anesthesiologist (how many rooms they're covering, by pairing type).
export interface DoctorSupervision {
  totalRooms: number;
  crnaRooms: number;         // rooms where they supervise a CRNA (cap = maxConcurrentNurseCoverageRatio, default 4)
  greenStudentRooms: number; // rooms where they supervise a GREEN student (cap = maxConcurrentStudentCoverageRatio, default 2)
  otherStudentRooms: number; // new/intermediate students (must be CRNA-paired or solo; don't change ratios)
  soloRooms: number;         // rooms alone
  concurrentRooms?: number;  // max rooms overlapping at once (kept for reference; not the signature count)
  signaturesUsed?: number;   // actual rooms the doctor is on = signatures used (same CRNA following to 2 rooms = 1)
}

interface EmployeeDetailsPopoverProps {
  employee: Employee;
  hospitalName?: string;
  shift?: {
    startTimeUtc: string;
    endTimeUtc: string;
    order?: number;
  };
  supervision?: DoctorSupervision;
  // Rooms this anesthesiologist ended up covering (drives the derived report-in time + the room synopsis).
  assignedRooms?: AssignedRoomInfo[];
  // Site coins for the hospital(s) this person is actually AT today — shown next to the name.
  atSites?: SiteCoinOption[];
  // Credentialed sites shown as colored coins (matching the board). When provided, replaces the plain pills.
  credentialSites?: SiteCoinOption[];
  // Pre-formatted "In" (report-in) label to display verbatim, so the card matches whatever the
  // Anesthesiologists tab shows (auto-derived OR the chief's manual override). Falls back to the derived time.
  inTimeLabel?: string;
  // When present (a staffed room placement), shows a recolor row. current = the active override color.
  recolor?: { current?: string; onPick: (color: string | null) => void };
  // When present (Anesthesiologists page), shows the Meeting / Request case / Off disposition control.
  statusControl?: DoctorStatusControl;
  // Editable site "coin" — click a site to hard-lock this person there for the day (or clear it).
  siteLock?: SiteLockControl;
  // Room transfer — move one of this doctor's rooms to another doctor (to hand-tune in-times).
  roomTransfer?: RoomTransferControl;
  // When provided, "Credentialed sites" is filtered to ONLY these names (the real current sites — on both
  // the Credentials page and the Hospitals & ORs page), so stale/renamed credentials never show on the board.
  validSiteNames?: string[];
  // Board doctor cards only: render the ACTIVITY-COIN header — a clickable coin beside the name, sliced by
  // supervising/solo + request + meeting, with the site shown as small text — and hide the status pill, the
  // "Set color" row, and the site-lock. Other cards (the Anesthesiologists tab, CRNAs) leave this off and keep
  // the classic header + controls.
  boardActivityCoin?: boolean;
  // Board only: "Assign relief" button (right of the coin) + a bullpen-style picker to place a relief nameplate.
  assignRelief?: AssignReliefControl;
  // Set ONLY when this card is opened as a transfer/swap TARGET from another person's picker. Renders a
  // prominent confirm button at the top ("Transfer all rooms here" / "Swap all rooms with X"); clicking it
  // fires onConfirm (the source card's move/swap). Lets the chief inspect the target's credentials first.
  transferAction?: { label: string; onConfirm: () => void; tone?: 'move' | 'swap' };
  children: React.ReactNode;
  onClick?: () => void;
  triggerStyle?: React.CSSProperties; // override the wrapper style (e.g. full-width block for a brick)
}

const EmployeeDetailsPopover: React.FC<EmployeeDetailsPopoverProps> = ({
  employee,
  hospitalName,
  shift,
  supervision,
  assignedRooms,
  atSites,
  credentialSites,
  inTimeLabel,
  recolor,
  statusControl,
  siteLock,
  roomTransfer,
  validSiteNames,
  boardActivityCoin,
  assignRelief,
  transferAction,
  children,
  onClick,
  triggerStyle
}) => {
  const [isClicked, setIsClicked] = useState(false);
  const [coinPickerOpen, setCoinPickerOpen] = useState(false);
  const [reliefPickerOpen, setReliefPickerOpen] = useState(false);
  const reliefBtnRef = useRef<HTMLButtonElement>(null);
  const [reliefPos, setReliefPos] = useState({ top: 0, left: 0, maxHeight: 400 });
  // Transfer/swap picker (replaces the old native dropdowns): which action is being picked, and where the
  // floating candidate panel sits. Cleared whenever the card closes.
  const [transferMode, setTransferMode] = useState<null | 'move' | 'swap'>(null);
  const [transferPos, setTransferPos] = useState({ top: 0, left: 0, maxHeight: 400 });
  const [position, setPosition] = useState({ top: 0, left: 0, maxHeight: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Calculate popover position
  useEffect(() => {
    if (isClicked && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const popoverWidth = 460; // estimated width (wider: credentials + room synopsis)
      const MARGIN = 20;

      // Default: show to the right
      let left = rect.right + 10;
      let top = rect.top;

      // Check if it would overflow the right edge
      if (left + popoverWidth > window.innerWidth) {
        // Show to the left instead
        left = rect.left - popoverWidth - 10;
      }

      // Anchor near the trigger's top, but never start off-screen. The card caps its own height
      // to whatever room is left below `top` and scrolls internally, so tall doctor cards (with the
      // open-signatures tally at the bottom) are always fully reachable.
      if (top < MARGIN) top = MARGIN;
      const maxHeight = window.innerHeight - top - MARGIN;

      setPosition({ top, left, maxHeight });
    }
  }, [isClicked]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // A tapped candidate opens THEIR card in a separate portal (also class "employee-details-popover"). Clicks
      // that land in ANY popover must not close this one, or opening a candidate's card would collapse the picker.
      const inSomePopover = !!(event.target as Element)?.closest?.('.employee-details-popover');
      if (
        isClicked &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        !inSomePopover
      ) {
        setIsClicked(false);
        setTransferMode(null);
      }
    };

    if (isClicked) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isClicked]);

  const handleClick = () => {
    if (isClicked) setTransferMode(null);   // closing the card also closes the transfer picker
    setIsClicked(!isClicked);
    if (onClick) {
      onClick();
    }
  };

  // Open (or toggle off) the transfer/swap candidate picker, positioned beside the card like the relief picker.
  const openTransferPicker = (mode: 'move' | 'swap') => {
    const card = popoverRef.current?.getBoundingClientRect();
    if (card) {
      const W = 272;
      let left = card.right + 8;
      if (left + W > window.innerWidth) left = card.left - W - 8;
      const top = Math.max(8, card.top);
      setTransferPos({ top, left: Math.max(8, left), maxHeight: Math.max(220, window.innerHeight - top - 16) });
    }
    setTransferMode(prev => (prev === mode ? null : mode));
  };
  const transferBtnStyle = (active: boolean): React.CSSProperties => ({
    fontSize: '0.78em', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap',
    background: active ? 'var(--or-blue-mid)' : 'var(--bg-input)', color: active ? '#fff' : 'var(--text-secondary)',
    border: `1px solid ${active ? 'var(--or-blue-mid)' : 'var(--popover-border)'}`,
  });

  const formatTime = (utcString: string) => {
    const date = new Date(utcString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Click-only: the card opens on click, never on hover.
  const showPopover = isClicked;

  // Credentialed sites to show: when validSiteNames is given (the board passes the current hospital names),
  // show ONLY credentials that are real current sites — so stale/renamed credentials never appear. Other
  // callers pass nothing and see the raw list.
  const shownCredentials = validSiteNames
    ? (employee.credentials || []).filter(c => validSiteNames.includes(c))
    : (employee.credentials || []);

  // ---- Derived, post-allocation info for an anesthesiologist ----
  const isDoctor = employee.employeeType === 'doctor';
  const isCrna = employee.employeeType === 'nurse';
  const isPecking = shift?.order !== undefined && shift?.order !== null;
  // "In" = the time they actually had to come in once the algorithm settled: 30 min before their EARLIEST assigned
  // room's first case (45 if that case needs a nerve block). Falls back to the raw shift start if not yet allocated.
  const roomsSorted = (assignedRooms || []).slice().sort((a, b) => a.startTimeUtc.localeCompare(b.startTimeUtc));
  const earliestRoom = roomsSorted[0];
  const reportInUtc = earliestRoom
    ? new Date(new Date(earliestRoom.startTimeUtc).getTime() - (earliestRoom.surgeryTypes.some(needsNerveBlock) ? NERVE_BLOCK_LEAD_MIN : REPORT_IN_LEAD_MIN) * 60000).toISOString()
    : undefined;
  // inTimeLabel (passed by the Anesthesiologists tab) wins so the card matches the tab's shown in-time exactly.
  // In-time = 30 min before the first assigned case (45 for a nerve block) for EVERY doctor, shift-work and
  // pecking alike (Braden 08-15). Only before allocation do we fall back to the raw shift start.
  const inLabel = inTimeLabel || (reportInUtc ? formatTime(reportInUtc) : (shift ? formatTime(shift.startTimeUtc) : '—'));
  // "Out": a shift-work doctor leaves at their shift end; a pecking-order doctor stays until the board clears -> TBD.
  const outLabel = isPecking ? 'TBD' : (shift ? formatTime(shift.endTimeUtc) : '—');

  // Supervising vs solo (drives the blue/orange status coin under the name + the signature dots).
  const supervisedRooms = supervision ? supervision.crnaRooms + supervision.greenStudentRooms + supervision.otherStudentRooms : 0;
  const isSupervising = supervisedRooms > 0;
  const isSoloDoc = !!supervision && supervision.soloRooms > 0 && supervisedRooms === 0;

  // A colored coin (site acronym on the hospital's own color) — the same coin used on the board & tabs.
  const renderCoin = (o: SiteCoinOption) => (
    <span key={o.hospitalId} title={o.name}
      style={{ fontSize: '0.72em', fontWeight: 800, letterSpacing: '0.3px', padding: '1px 8px', borderRadius: '10px',
        background: o.color, color: '#fff', border: `1px solid ${darkenHex(o.color, 0.2)}`,
        textShadow: '0 1px 1px rgba(0,0,0,0.35)', whiteSpace: 'nowrap' }}>{o.acronym}</span>
  );

  // Signatures scorecard (orange dots) — shown only when SUPERVISING. One bubble per signature slot
  // (cap = max concurrent CRNA coverage, default 4); a filled bubble = a room they're supervising. A solo
  // doctor gets no dots — the orange "In room" status coin conveys that instead.
  const scorecardNode = (isDoctor && isSupervising && supervision) ? (() => {
    const cap = employee.maxConcurrentNurseCoverageRatio ?? 4;
    const used = supervision.signaturesUsed ?? supervision.concurrentRooms ?? supervision.totalRooms;
    const filled = Math.min(used, cap);
    const open = Math.max(0, cap - used);
    return (
      <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '7px' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.85em', fontWeight: 600 }}>Signatures available</span>
          <span style={{ fontSize: '0.8em', fontWeight: 700, color: open > 0 ? 'var(--amber)' : 'var(--text-faint)' }}>{open} open</span>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }} aria-label={`${open} of ${cap} signatures open`}>
          {Array.from({ length: cap }).map((_, i) => {
            const isFilled = i < filled;
            return (
              <span key={i} title={isFilled ? 'Signature used (assigned room)' : 'Open signature'}
                style={{ width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                  background: isFilled ? 'var(--amber)' : 'var(--bg-input)',
                  border: isFilled ? '1px solid rgba(120, 60, 0, 0.55)' : '1px solid var(--border-soft)',
                  boxShadow: isFilled ? '0 1px 2px rgba(0,0,0,0.30)' : 'inset 0 2px 4px rgba(0,0,0,0.45)',
                  opacity: isFilled ? 1 : 0.7 }} />
            );
          })}
        </div>
      </div>
    );
  })() : null;

  // The status "coin" under the name: blue "Supervising" or orange "In room" for a placed doctor; otherwise
  // fall back to the plain role pill (non-doctors, or a doctor with no determinable placement yet).
  const statusOrPillNode = (isDoctor && (isSupervising || isSoloDoc)) ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 12px', borderRadius: 'var(--radius-pill)',
      background: isSupervising ? SUPERVISE_COLOR : SOLO_COLOR, color: '#fff', fontWeight: 700, fontSize: '0.82em',
      letterSpacing: '0.3px', boxShadow: '0 1px 2px rgba(0,0,0,0.25)' }}>
      {isSupervising ? 'Supervising' : 'In room'}
    </span>
  ) : (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 'var(--radius-pill)',
      backgroundColor: employee.employeeType === 'doctor' ? 'rgba(74, 159, 212, 0.16)' :
                     employee.employeeType === 'nurse' ? 'rgba(217, 119, 6, 0.16)' : 'rgba(22, 163, 74, 0.16)',
      color: employee.employeeType === 'doctor' ? 'var(--or-blue-mid)' :
             employee.employeeType === 'nurse' ? 'var(--amber)' : 'var(--success-text)',
      fontWeight: 500, fontSize: '0.85em', letterSpacing: '0.02em'
    }}>
      {getEmployeeTypeDisplayName(employee.employeeType)}
    </span>
  );

  // ---- Board activity coin (doctor cards on the board) ----
  // The coin beside the name shows the SITE acronym(s) they're at today, tinted by what they're doing:
  // supervising (blue) or solo (orange) from their placement, plus request (periwinkle) and/or meeting (brown)
  // from the chief's disposition. One designation → solid; several → an even horizontal split. "Off" greys the
  // card instead of taking a slice. A manual color override (recolor) wins and fills the whole coin; clicking
  // the coin opens the swatches.
  const dispositionStatuses: DoctorDayStatus[] = statusControl?.statuses || [];
  const activitySlices: { key: string; color: string; label: string }[] = [];
  if (isSupervising) activitySlices.push({ key: 'supervising', color: SUPERVISE_COLOR, label: 'Supervising' });
  else if (isSoloDoc) activitySlices.push({ key: 'solo', color: SOLO_COLOR, label: 'In room' });
  if (dispositionStatuses.includes('request')) activitySlices.push({ key: 'request', color: REQUEST_COLOR, label: 'Request case' });
  if (dispositionStatuses.includes('meeting')) activitySlices.push({ key: 'meeting', color: MEETING_COLOR, label: 'Meeting' });
  const coinOverride = recolor?.current;
  // No designation (e.g. a CRNA has no supervising/solo) → tint the coin with the hospital/site color.
  const siteColor = (atSites && atSites[0]?.color) || undefined;
  const coinHasColor = !!coinOverride || activitySlices.length > 0 || !!siteColor;
  const activityCoinBg = coinOverride
    ? coinOverride
    : activitySlices.length === 0
      ? (siteColor || 'var(--bg-input)')
      : activitySlices.length === 1
        ? activitySlices[0].color
        : `linear-gradient(90deg, ${activitySlices
            .map((s, i) => `${s.color} ${((i * 100) / activitySlices.length).toFixed(2)}% ${(((i + 1) * 100) / activitySlices.length).toFixed(2)}%`)
            .join(', ')})`;
  // Site initials shown ON the coin (kept even as the color reflects designation).
  const coinAcronym = (atSites || []).map(s => s.acronym).join(' · ');
  // The coin only recolors when a placement was passed (`recolor`) — a bullpen brick or an inherited plate has
  // nothing to recolor. Keep the tooltip and the screen-reader label honest about that instead of inviting a
  // click that does nothing.
  const coinAction = [recolor ? 'set color' : '', siteLock ? 'lock to a site' : ''].filter(Boolean).join(' / ');
  const activityCoinTitle = [
    (atSites || []).map(s => s.name).join(', '),
    coinOverride ? 'custom color' : activitySlices.map(s => s.label).join(' · '),
  ].filter(Boolean).join(' — ') || (coinAction ? `Click to ${coinAction}` : 'No site or designation yet');
  const activityCoinAriaLabel = [
    `Site ${coinAcronym || 'none'}.`,
    `${activitySlices.map(s => s.label).join(', ') || 'No designation'}.`,
    coinAction ? `Click to ${coinAction}.` : '',
  ].filter(Boolean).join(' ');

  // Meeting / Request case / Off disposition buttons (Anesthesiologists page).
  const STATUS_BUTTONS: { key: DoctorDayStatus; label: string; color: string }[] = [
    { key: 'meeting', label: 'Meeting', color: MEETING_COLOR },
    { key: 'request', label: 'Request case', color: REQUEST_COLOR },
    { key: 'off', label: 'Off', color: OFF_COLOR },
  ];

  return (
    <>
      <div
        ref={triggerRef}
        className="employee-details-trigger"
        onClick={handleClick}
        style={{
          cursor: 'pointer',
          display: 'inline-block',
          ...triggerStyle
        }}
      >
        {children}
      </div>

      {showPopover && ReactDOM.createPortal((
        <div
          ref={popoverRef}
          className="employee-details-popover"
          style={{
            // Portaled to <body> and position:fixed so it always sits above the board and stays
            // put in the forefront while the page/board scrolls behind it. Opaque background.
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            // A candidate card opened FROM a picker (transferAction set) must sit above the picker (100001).
            zIndex: transferAction ? 100002 : 100000,
            backgroundColor: 'var(--popover-bg)',
            border: '1px solid var(--popover-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            padding: '16px',
            minWidth: '340px',
            maxWidth: '460px',
            maxHeight: `${position.maxHeight}px`,
            overflowY: 'auto',
            fontSize: '0.9em',
            color: 'var(--text-primary)',
            animation: 'fadeIn 0.2s ease-in-out'
          }}
        >
          {/* Header. Board doctor cards: name + a clickable ACTIVITY COIN (sliced by supervising/solo + request +
              meeting), with the site as small text. Everywhere else: name + site coin(s) + the status pill. */}
          <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--border-subtle)' }}>
            {boardActivityCoin && (isDoctor || isCrna) ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '1.1em', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                    {employee.firstName} {employee.lastName}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); if (recolor || siteLock) setCoinPickerOpen(o => !o); }}
                    title={activityCoinTitle}
                    aria-label={activityCoinAriaLabel}
                    aria-disabled={!(recolor || siteLock)}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      minWidth: '30px', padding: '2px 9px', borderRadius: '11px', background: activityCoinBg,
                      color: coinHasColor ? '#fff' : 'var(--text-secondary)',
                      textShadow: coinHasColor ? '0 1px 1px rgba(0,0,0,0.35)' : 'none',
                      fontSize: '0.74em', fontWeight: 800, letterSpacing: '0.3px', whiteSpace: 'nowrap',
                      cursor: (recolor || siteLock) ? 'pointer' : 'default',
                      border: coinHasColor ? '1px solid rgba(0,0,0,0.30)' : '1px solid var(--border-soft)',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.25)' }}
                  >
                    {coinAcronym || '—'}
                  </button>
                  {assignRelief && (
                    <>
                      <button
                        ref={reliefBtnRef}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); const card = popoverRef.current?.getBoundingClientRect(); if (card) { const W = 214; let left = card.right + 8; if (left + W > window.innerWidth) left = card.left - W - 8; const top = Math.max(8, card.top); setReliefPos({ top, left: Math.max(8, left), maxHeight: Math.max(160, window.innerHeight - top - 16) }); } setReliefPickerOpen(o => !o); }}
                        title={assignRelief.current ? `Relief: ${assignRelief.current.name} — click to change` : 'Assign a relief nameplate above this person (this OR slot only)'}
                        style={{ fontSize: '0.72em', fontWeight: 700, padding: '3px 9px', borderRadius: '10px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                          background: assignRelief.current ? 'var(--or-blue-mid)' : 'transparent',
                          color: assignRelief.current ? '#fff' : 'var(--text-secondary)',
                          border: `1px solid ${assignRelief.current ? 'var(--or-blue-mid)' : 'var(--popover-border)'}` }}
                      >
                        {assignRelief.current ? `Relief: ${assignRelief.current.name}` : '+ Assign relief'}
                      </button>
                      {assignRelief.current && (
                        <button type="button" title="Clear relief" onClick={(e) => { e.stopPropagation(); assignRelief.onClear(); setReliefPickerOpen(false); }}
                          style={{ fontSize: '0.8em', lineHeight: 1, padding: '2px 6px', borderRadius: '8px', cursor: 'pointer', background: 'transparent', color: 'var(--text-faint)', border: '1px solid var(--popover-border)', flexShrink: 0 }}>✕</button>
                      )}
                    </>
                  )}
                </div>
                {reliefPickerOpen && assignRelief && (
                  // Separate floating popover: a colored, list-wise roster grouped by pecking order / shift window.
                  <div onClick={e => e.stopPropagation()}
                    style={{ position: 'fixed', top: `${reliefPos.top}px`, left: `${reliefPos.left}px`, zIndex: 100001,
                      width: '214px', maxHeight: `${reliefPos.maxHeight}px`, overflowY: 'auto', background: 'var(--popover-bg)',
                      border: '1px solid var(--popover-border)', borderRadius: '9px', boxShadow: 'var(--shadow-lg)', padding: '7px 8px' }}>
                    <div style={{ fontSize: '0.72em', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '6px' }}>Assign relief</div>
                    {assignRelief.groups.length === 0 ? (
                      <div style={{ fontSize: '0.78em', color: 'var(--text-faint)', fontStyle: 'italic' }}>No one available.</div>
                    ) : assignRelief.groups.map(g => (
                      <div key={g.label} style={{ marginBottom: '7px' }}>
                        <div style={{ fontSize: '0.64em', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-faint)', marginBottom: '3px' }}>{g.label}</div>
                        {g.items.map(it => (
                          <button key={it.id} type="button"
                            onClick={(e) => { e.stopPropagation(); assignRelief.onAssign(it.id); setReliefPickerOpen(false); setIsClicked(false); }}
                            title={`Assign ${it.name} as relief`}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', textAlign: 'left', marginBottom: '2px',
                              fontSize: '0.8em', fontWeight: 600, padding: '4px 8px', borderRadius: '5px', cursor: 'pointer',
                              background: it.color || 'var(--bg-surface)', color: it.color ? '#fff' : 'var(--text-secondary)',
                              textShadow: it.color ? '0 1px 1px rgba(0,0,0,0.35)' : 'none', border: '1px solid rgba(0,0,0,0.25)' }}>
                            {it.note && <span style={{ fontWeight: 800, opacity: 0.9, flexShrink: 0 }}>{it.note}</span>}
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.name}</span>
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {coinPickerOpen && (recolor || siteLock) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                    {recolor && (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {recolorOptionsFor(employee.employeeType).map(opt => (
                          <button key={opt.label} title={opt.label} onClick={() => { recolor.onPick(opt.color); setCoinPickerOpen(false); }}
                            style={{ width: '18px', height: '18px', borderRadius: '4px', background: opt.color, padding: 0, cursor: 'pointer',
                              border: recolor.current === opt.color ? '2px solid var(--text-primary)' : '1px solid rgba(0,0,0,0.3)' }} />
                        ))}
                        <button title="Back to automatic activity colors" onClick={() => { recolor.onPick(null); setCoinPickerOpen(false); }}
                          style={{ fontSize: '0.72em', padding: '2px 8px', borderRadius: '4px', border: '1px solid var(--popover-border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                          Auto
                        </button>
                      </div>
                    )}
                    {siteLock && (
                      // B4: doctors/CRNAs are hard-locked to a site FROM THE COIN HEADER (the standalone site-lock
                      // block below is hidden for them because boardActivityCoin is set). Click a site to lock for the
                      // day, or the highlighted one to clear.
                      <div>
                        <div style={{ fontSize: '0.72em', color: 'var(--text-muted)', marginBottom: '4px' }}>
                          Lock to a site for the day <span style={{ color: 'var(--text-faint)' }}>(used only here)</span>:
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                          {siteLock.options.map(o => {
                            const active = siteLock.current === o.hospitalId;
                            return (
                              <button key={o.hospitalId} title={active ? `Locked to ${o.name} — click to clear` : `Lock to ${o.name}`}
                                onClick={() => siteLock.onPick(active ? null : o.hospitalId)}
                                style={{ fontSize: '0.7em', fontWeight: 800, letterSpacing: '0.3px', padding: '2px 8px', borderRadius: '10px', cursor: 'pointer',
                                  background: active ? o.color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
                                  border: `1px solid ${o.color}`, textShadow: active ? '0 1px 1px rgba(0,0,0,0.35)' : 'none', whiteSpace: 'nowrap' }}>
                                {active ? '🔒 ' : ''}{o.acronym}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                  <span style={{ fontSize: '1.1em', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                    {employee.firstName} {employee.lastName}
                  </span>
                  {atSites && atSites.map(renderCoin)}
                </div>
                <div>{statusOrPillNode}</div>
              </>
            )}
          </div>

          {/* Transfer/swap confirm — only when this card was opened as a target from a picker. Sits at the top
              so the chief can eyeball credentials/rooms below, then commit. */}
          {transferAction && (
            <button type="button"
              onClick={(e) => { e.stopPropagation(); transferAction.onConfirm(); }}
              style={{ display: 'block', width: '100%', marginBottom: '12px', padding: '9px 10px', borderRadius: '8px',
                cursor: 'pointer', fontSize: '0.92em', fontWeight: 800, color: '#fff', border: 'none',
                background: transferAction.tone === 'swap' ? 'var(--or-blue-mid)' : '#ff8100',
                boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }}>
              {transferAction.label}
            </button>
          )}

          {/* Signatures scorecard (orange dots) — right under the name so it's the first thing you see. */}
          {scorecardNode}

          {/* Disposition (Anesthesiologists page): Meeting / Request case / Off. Meeting & Request reveal a
              notes box whose text surfaces under the name on the tab; Off greys the name out (sent home). */}
          {statusControl && (isDoctor || isCrna) && (
            <div style={{ paddingBottom: '10px', marginBottom: '10px', borderBottom: '1px solid var(--popover-border)' }}>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {STATUS_BUTTONS.map(b => {
                  const active = statusControl.statuses.includes(b.key);
                  return (
                    <button
                      key={b.key}
                      onClick={() => statusControl.onToggleStatus(b.key)}
                      title={active ? `Clear "${b.label}"` : b.label}
                      style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '0.8em', fontWeight: 700, padding: '4px 11px', borderRadius: '6px', cursor: 'pointer',
                        background: active ? b.color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)', border: `1px solid ${b.color}` }}
                    >
                      <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: b.color, border: '1px solid rgba(255,255,255,0.5)' }} />
                      {b.label}{active ? ' ✓' : ''}
                    </button>
                  );
                })}
              </div>
              {(statusControl.statuses.includes('meeting') || statusControl.statuses.includes('request')) && (
                <textarea
                  value={statusControl.note}
                  onChange={e => statusControl.onSetNote(e.target.value)}
                  placeholder={statusControl.statuses.includes('meeting') ? 'Notes about the meeting…' : 'Notes about the request case…'}
                  className="form-control"
                  style={{ marginTop: '8px', width: '100%', minHeight: '52px', fontSize: '0.82em', padding: '6px 8px', resize: 'vertical' }}
                />
              )}
              {statusControl.statuses.includes('off') && (
                <div style={{ marginTop: '8px', fontSize: '0.78em', color: 'var(--text-faint)', fontStyle: 'italic' }}>
                  Sent home — their name stays on the list, greyed out. Click "Off" again to call them back in.
                </div>
              )}
            </div>
          )}

          {/* Recolor row (staffed placements): click a role-appropriate swatch (e.g. a request case).
              Board doctor cards recolor via the activity coin instead, so this row is hidden there. */}
          {recolor && !boardActivityCoin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', paddingBottom: '10px', marginBottom: '10px', borderBottom: '1px solid var(--popover-border)' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.8em', marginRight: '2px' }}>Set color:</span>
              {recolorOptionsFor(employee.employeeType).map(opt => (
                <button
                  key={opt.label}
                  title={opt.label}
                  onClick={() => recolor.onPick(opt.color)}
                  style={{
                    width: '18px', height: '18px', borderRadius: '4px', background: opt.color, padding: 0, cursor: 'pointer',
                    border: recolor.current === opt.color ? '2px solid var(--text-primary)' : '1px solid rgba(0,0,0,0.3)',
                  }}
                />
              ))}
              <button
                title="Reset to default color"
                onClick={() => recolor.onPick(null)}
                style={{ fontSize: '0.72em', padding: '2px 7px', borderRadius: '4px', border: '1px solid var(--popover-border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}
              >
                Reset
              </button>
            </div>
          )}

          {/* Editable site coin — click a site to hard-lock this person there for the day (or clear it).
              Removed from the board doctor card per the redesign; still available elsewhere (e.g. CRNAs). */}
          {siteLock && !boardActivityCoin && (
            <div style={{ paddingBottom: '10px', marginBottom: '10px', borderBottom: '1px solid var(--popover-border)' }}>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.8em', marginBottom: '6px' }}>
                Site for the day <span style={{ color: 'var(--text-faint)' }}>(hard lock — used only here)</span>:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {siteLock.options.map(o => {
                  const active = siteLock.current === o.hospitalId;
                  return (
                    <button
                      key={o.hospitalId}
                      title={active ? `Locked to ${o.name} — click to clear` : `Lock to ${o.name}`}
                      onClick={() => siteLock.onPick(active ? null : o.hospitalId)}
                      style={{ fontSize: '0.7em', fontWeight: 800, letterSpacing: '0.3px', padding: '2px 8px', borderRadius: '10px', cursor: 'pointer',
                        background: active ? o.color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
                        border: `1px solid ${o.color}`, textShadow: active ? '0 1px 1px rgba(0,0,0,0.35)' : 'none', whiteSpace: 'nowrap' }}
                    >
                      {active ? '🔒 ' : ''}{o.acronym}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: '0.72em', color: 'var(--text-faint)', marginTop: '5px' }}>
                {siteLock.current ? 'Locked. Click the highlighted site to clear.' : 'Click a site to pin them there for the day.'}
              </div>
            </div>
          )}

          {/* Details */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Home Hospital */}
            {hospitalName && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.85em' }}>Home Hospital:</span>
                <span style={{ fontWeight: '500', fontSize: '0.85em', textAlign: 'right', maxWidth: '60%' }}>
                  {hospitalName}
                </span>
              </div>
            )}

            {/* Credentialed sites (the "spots" this provider can work) — colored site coins when available. */}
            <div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85em', marginBottom: '4px' }}>Credentialed sites:</div>
              {credentialSites ? (
                credentialSites.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {credentialSites.map(renderCoin)}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.8em', color: 'var(--text-faint)' }}>No site-specific credentials (eligible for open sites)</div>
                )
              ) : shownCredentials.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {shownCredentials.map((cred, idx) => (
                    <span
                      key={idx}
                      style={{
                        display: 'inline-block',
                        padding: '2px 6px',
                        backgroundColor: 'rgba(74, 159, 212, 0.15)',
                        color: 'var(--or-blue-mid)',
                        border: '1px solid #bae6fd',
                        borderRadius: '4px',
                        fontSize: '0.75em',
                        fontWeight: '500'
                      }}
                    >
                      {cred}
                    </span>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '0.8em', color: 'var(--text-faint)' }}>No site-specific credentials (eligible for open sites)</div>
              )}
            </div>

            {/* Anesthesiologist / CRNA In/Out — In matches the tab's derived report-in (auto or manual); Out is the
                shift end for a shift-work provider, or TBD for a pecking-order one (they stay until the board clears). */}
            {(isDoctor || isCrna) && (shift || roomsSorted.length > 0 || inTimeLabel) && (
              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--popover-border)' }}>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85em', marginBottom: '4px', fontWeight: 600 }}>Shift</div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85em' }}>In (report-in):</span>
                  <span style={{ fontWeight: 500, fontSize: '0.85em' }}>{inLabel}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85em' }}>Out:</span>
                  <span style={{ fontWeight: 500, fontSize: '0.85em', color: outLabel === 'TBD' ? 'var(--text-faint)' : 'var(--text-primary)', fontStyle: outLabel === 'TBD' ? 'italic' : 'normal' }}>{outLabel}</span>
                </div>
                {isPecking && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.85em' }}>Priority Order:</span>
                    <span style={{ fontWeight: 'bold', fontSize: '0.85em', backgroundColor: 'rgba(217, 119, 6, 0.18)', color: 'var(--amber)', padding: '2px 8px', borderRadius: '12px' }}>#{shift?.order}</span>
                  </div>
                )}
              </div>
            )}

            {/* Raw shift window for students — shown ABOVE their rooms. (CRNAs now use the derived In/Out block above.) */}
            {!isDoctor && !isCrna && shift && (
              <>
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--popover-border)' }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85em', marginBottom: '4px', fontWeight: '600' }}>Shift Details:</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85em' }}>Start Time:</span>
                  <span style={{ fontWeight: '500', fontSize: '0.85em' }}>{formatTime(shift.startTimeUtc)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85em' }}>End Time:</span>
                  <span style={{ fontWeight: '500', fontSize: '0.85em' }}>{formatTime(shift.endTimeUtc)}</span>
                </div>
              </>
            )}

            {/* Rooms this person is in. Per OR: the OR label (+ "· solo" when no CRNA), then a compact list of
                that room's CASES — each case's START TIME on the left, case name to its right (nerve-block yellow). */}
            {(isDoctor || isCrna) && roomsSorted.length > 0 && (
              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--popover-border)' }}>
                {/* Rooms header + two bulk actions: "Transfer all" hands EVERY room to one other doctor;
                    "Swap all" EXCHANGES all rooms with another doctor (swaps their in-times too). */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85em', fontWeight: 600 }}>Rooms ({roomsSorted.length})</span>
                  {roomTransfer && roomTransfer.candidates.length > 0 && (
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <button type="button"
                        title="Hand ALL of this person's rooms to someone else"
                        onClick={e => { e.stopPropagation(); openTransferPicker('move'); }}
                        style={transferBtnStyle(transferMode === 'move')}>
                        Transfer all →
                      </button>
                      <button type="button"
                        title="Swap ALL room assignments with someone else (exchanges both people's rooms — and their in-times)"
                        onClick={e => { e.stopPropagation(); openTransferPicker('swap'); }}
                        style={transferBtnStyle(transferMode === 'swap')}>
                        Swap all ⇄
                      </button>
                    </div>
                  )}
                </div>
                {/* Transfer/swap candidate picker — a floating panel (like "Assign relief") with a STICKY source
                    header (who you're moving + their site coins), candidates GROUPED under colored site coins, and
                    each name TAPPABLE to open their staff card (credentials) where the move/swap is confirmed. */}
                {transferMode && roomTransfer && (
                  <div className="employee-details-popover" onClick={e => e.stopPropagation()}
                    style={{ position: 'fixed', top: `${transferPos.top}px`, left: `${transferPos.left}px`, zIndex: 100001,
                      width: '272px', maxHeight: `${transferPos.maxHeight}px`, overflowY: 'auto', background: 'var(--popover-bg)',
                      border: '1px solid var(--popover-border)', borderRadius: '10px', boxShadow: 'var(--shadow-lg)', padding: '0 9px 9px' }}>
                    {/* STICKY: the person being moved stays pinned as you scroll the candidate list. */}
                    <div style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--popover-bg)', paddingTop: '9px', paddingBottom: '7px', marginBottom: '7px', borderBottom: '1px solid var(--popover-border)' }}>
                      <div style={{ fontSize: '0.62em', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-faint)' }}>
                        {transferMode === 'move' ? 'Transfer all rooms from' : 'Swap all rooms with'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '3px' }}>
                        <span style={{ fontWeight: 700, fontSize: '1.02em', color: 'var(--text-primary)' }}>{employee.firstName} {employee.lastName}</span>
                        {atSites && atSites.map(renderCoin)}
                      </div>
                      <div style={{ fontSize: '0.66em', color: 'var(--text-faint)', marginTop: '3px' }}>
                        {roomTransfer.getCandidateCard ? 'Tap a name to see their card, then confirm' : `Tap a name to ${transferMode === 'move' ? 'transfer' : 'swap'}`}
                      </div>
                    </div>
                    {(roomTransfer.candidateGroups && roomTransfer.candidateGroups.length
                      ? roomTransfer.candidateGroups
                      : [{ label: '', docs: roomTransfer.candidates.map(c => ({ id: c.id, name: c.name, order: null }) as TransferCandidateEntry) } as TransferCandidateGroup]
                    ).map((g, gi) => (
                      <div key={g.hospitalId || g.label || `g${gi}`} style={{ marginBottom: '9px' }}>
                        {(g.color || g.label) && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            {g.color && (
                              <span style={{ fontSize: '0.7em', fontWeight: 800, letterSpacing: '0.3px', padding: '1px 8px', borderRadius: '10px', background: g.color, color: '#fff', border: `1px solid ${darkenHex(g.color, 0.2)}`, textShadow: '0 1px 1px rgba(0,0,0,0.35)', whiteSpace: 'nowrap' }}>{g.acronym || '—'}</span>
                            )}
                            <span style={{ fontSize: '0.64em', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-faint)' }}>{g.label}</span>
                          </div>
                        )}
                        {g.docs.map(c => {
                          const doTrade = () => { if (transferMode === 'move') roomTransfer.onMoveAll(c.id); else roomTransfer.onSwapAll(c.id); setTransferMode(null); setIsClicked(false); };
                          const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '7px', width: '100%', textAlign: 'left', marginBottom: '2px', fontSize: '0.82em', fontWeight: 600, padding: '5px 8px', borderRadius: '6px', cursor: 'pointer', background: 'var(--bg-surface)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' };
                          const rowInner = (
                            <>
                              {c.order != null && <span style={{ fontSize: '0.78em', fontWeight: 800, color: 'var(--amber)', minWidth: '16px', textAlign: 'right', flexShrink: 0 }} title="Pecking order #">{c.order}</span>}
                              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{c.name}</span>
                            </>
                          );
                          const cardData = roomTransfer.getCandidateCard?.(c.id);
                          return cardData ? (
                            <EmployeeDetailsPopover key={c.id} {...cardData}
                              transferAction={{ label: transferMode === 'move' ? 'Transfer all rooms here' : `Swap all rooms with ${employee.firstName} ${employee.lastName}`, tone: transferMode, onConfirm: doTrade }}
                              triggerStyle={{ display: 'block', width: '100%' }}>
                              <span style={rowStyle}>{rowInner}</span>
                            </EmployeeDetailsPopover>
                          ) : (
                            <button key={c.id} type="button" onClick={e => { e.stopPropagation(); doTrade(); }} style={rowStyle}>{rowInner}</button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {roomsSorted.map((r, i) => {
                    const cases = (r.cases && r.cases.length) ? r.cases : [{ startTimeUtc: r.startTimeUtc, surgeryType: r.surgeryTypes.join(', ') }];
                    return (
                      <div key={i} style={{ fontSize: '0.82em' }}>
                        {/* OR label row (+ solo tag, + optional room transfer on the right). */}
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                          <span style={{ fontWeight: 700 }}>{r.roomLabel}</span>
                          {!r.hasCrna && <span style={{ color: NAMEPLATE_COLORS.docSolo, fontWeight: 700, fontSize: '0.9em' }}>· solo</span>}
                          {roomTransfer && (() => {
                            // Site-scoped per-room targets (doctors at THIS room's site); fall back to the all-actions list.
                            const roomCands = roomTransfer.candidatesForRoom ? roomTransfer.candidatesForRoom(r.operatingRoomId) : roomTransfer.candidates;
                            if (roomCands.length === 0) return null;
                            const selStyle: React.CSSProperties = { fontSize: '0.9em', padding: '1px 4px', borderRadius: '4px', border: '1px solid var(--popover-border)', background: 'var(--bg-input)', color: 'var(--text-secondary)', cursor: 'pointer', maxWidth: '96px' };
                            return (
                              <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '4px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                                <select value="" title="Move this room to another doctor at this site"
                                  onChange={e => { if (e.target.value) { roomTransfer.onMove(r.operatingRoomId, e.target.value); setIsClicked(false); } }}
                                  style={selStyle}>
                                  <option value="">move →</option>
                                  {roomCands.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                                {roomTransfer.onSwapRoom && (
                                  <select value="" title="Swap this room with another doctor at this site (you take their earliest room here in return)"
                                    onChange={e => { if (e.target.value && roomTransfer.onSwapRoom) { roomTransfer.onSwapRoom(r.operatingRoomId, e.target.value); setIsClicked(false); } }}
                                    style={selStyle}>
                                    <option value="">swap ⇄</option>
                                    {roomCands.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                  </select>
                                )}
                              </span>
                            );
                          })()}
                        </div>
                        {/* Cases: START TIME on the left, then the case name. A case needing a nerve block
                            is shown in the nerve-block yellow. */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', paddingLeft: '10px', marginTop: '2px' }}>
                          {cases.map((c, j) => {
                            const nb = needsNerveBlock(c.surgeryType);
                            return (
                              <div key={j} style={{ display: 'flex', gap: '8px', lineHeight: 1.35 }}>
                                <span style={{ color: 'var(--text-secondary)', minWidth: '58px', flexShrink: 0, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{formatTime(c.startTimeUtc)}</span>
                                <span style={{ color: nb ? NERVE_BLOCK_BORDER : 'var(--text-muted)', fontWeight: nb ? 700 : undefined, minWidth: 0 }}>{c.surgeryType || '—'}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>

          {/* Footer hint */}
          {isClicked && (
            <div style={{
              marginTop: '12px',
              paddingTop: '8px',
              borderTop: '1px solid var(--popover-border)',
              fontSize: '0.75em',
              color: 'var(--text-faint)',
              textAlign: 'center'
            }}>
              Click outside to close
            </div>
          )}
        </div>
      ), document.body)}

      {/* CSS animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default EmployeeDetailsPopover;
